import React, { useMemo, useState } from 'react';
import Table from '../../components/Table';
import Button from '../../components/Button';
import PlusIcon from 'assets/images/icon/plus-white.png';
import Input from '../../components/Input';
import search from 'assets/images/icon/search-normal.png';
import ToggleSwitch from '../../components/ToggleSwitch';
import Card from '../../components/Card';
import { useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import PersonIcon from 'assets/images/icon/person-icon';
import PersonsIcon from 'assets/images/icon/persons-icon';
import UserChecked from 'assets/images/icon/user-checked';
import UserDisabled from 'assets/images/icon/user-disabled';
import { stringMerger } from 'utils';
import { useQuery } from '@tanstack/react-query';
import { getContractors } from 'services/contractors';
import axiosInstance from '../../axios/axiosInstance';

const ONBOARDING_API = 'https://onboarding-api.payrollnoot.com';

function ContractorInviteModal({ onClose }) {
    const [emails, setEmails]   = useState('');
    const [sending, setSending] = useState(false);
    const [results, setResults] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [serviceType, setServiceType] = useState('');
    const [locationId, setLocationId] = useState(localStorage.getItem('locationId') || '');

    const companyId = localStorage.getItem('companyId');
    const lockedLocationId = localStorage.getItem('locationId') || '';
    const { data: locationsRaw = [] } = useQuery({
        queryKey: ['company-locations', companyId],
        queryFn: () => axiosInstance.get(`/companies/${companyId}/locations`).then(r => r.data),
        enabled: !!companyId,
        retry: false,
    });
    const locations = (Array.isArray(locationsRaw) ? locationsRaw : []).filter(l => l.isActive !== false);
    const requireLocation = locations.length > 0;

    const emailList  = emails.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
    const emailCount = emailList.length;
    const inputSt    = { width:'100%', padding:'8px 12px', borderRadius:8, border:'1.5px solid #e5e7eb', fontSize:13, outline:'none', boxSizing:'border-box' };

    const handleSend = async () => {
        if (!emailCount) return;
        if (requireLocation && !String(locationId || '').trim()) {
            setResults({ error: 'Branch / Location is required before sending invites.' });
            return;
        }
        setSending(true);
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
            const resp  = await axiosInstance.post(
                `${ONBOARDING_API}/api/invites/bulk-send`,
                {
                    emails: emailList,
                    type:   'contractor',
                    onboardingBaseUrl: 'https://onboarding.payrollnoot.com',
                    startDate:   startDate   || undefined,
                    department:  serviceType || undefined,
                    locationId:  locationId.trim() || undefined,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setResults(resp.data);
        } catch (err) {
            setResults({ error: err?.response?.data?.message || 'Failed to send invites.' });
        } finally {
            setSending(false);
        }
    };

    return (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:'#fff', borderRadius:20, padding:'36px 32px', maxWidth:520, width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.22)', maxHeight:'90vh', overflowY:'auto' }}>

                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                        <div style={{ width:48, height:48, borderRadius:12, background:'#EFF6FF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>✉️</div>
                        <div>
                            <h5 style={{ fontWeight:700, margin:0, fontSize:17, color:'#1a1a2e' }}>Send Contractor Onboarding Invites</h5>
                            <p style={{ color:'#888', fontSize:13, margin:'2px 0 0' }}>Contractors receive a unique invite link by email</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:'#aaa', cursor:'pointer', lineHeight:1, padding:0 }}>✕</button>
                </div>

                {!results ? (
                    <>
                        <div style={{ marginBottom:20 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                                <label style={{ fontWeight:600, fontSize:13, color:'#444' }}>Contractor email addresses</label>
                                {emailCount > 0 && (
                                    <span style={{ background:'#1B75F0', color:'#fff', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:600 }}>
                                        {emailCount} contractor{emailCount !== 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                            <textarea
                                rows={4}
                                className="form-control"
                                placeholder={"contractor1@example.com\ncontractor2@example.com"}
                                value={emails}
                                onChange={e => setEmails(e.target.value)}
                                style={{ fontSize:13, resize:'vertical', borderRadius:10, border:'1.5px solid #e5e7eb', padding:'10px 12px', width:'100%', boxSizing:'border-box' }}
                            />
                            <p style={{ fontSize:12, color:'#aaa', marginTop:5 }}>One per line, or comma/semicolon-separated</p>
                        </div>

                        {requireLocation && (
                            <div style={{ marginBottom:20 }}>
                                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#555', marginBottom:5 }}>Branch / Location *</label>
                                <select
                                    value={locationId}
                                    onChange={e => setLocationId(e.target.value)}
                                    disabled={!!lockedLocationId}
                                    style={{ ...inputSt, background:'#fff' }}
                                >
                                    <option value="">Select branch / location…</option>
                                    {locations.map(l => (
                                        <option key={l.id} value={l.id}>{l.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div style={{ marginBottom:20, display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                            <div>
                                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#555', marginBottom:5 }}>Service / Work Type <span style={{ color:'#aaa', fontWeight:400 }}>(optional)</span></label>
                                <select value={serviceType} onChange={e => setServiceType(e.target.value)} style={{ ...inputSt, background:'#fff' }}>
                                    <option value="">Select type</option>
                                    <option>Technology / IT</option>
                                    <option>Marketing / Design</option>
                                    <option>Consulting</option>
                                    <option>Finance / Accounting</option>
                                    <option>Legal</option>
                                    <option>Construction / Trades</option>
                                    <option>Healthcare</option>
                                    <option>Writing / Content</option>
                                    <option>Other</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#555', marginBottom:5 }}>Start Date <span style={{ color:'#aaa', fontWeight:400 }}>(optional)</span></label>
                                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inputSt} />
                            </div>
                        </div>

                        <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'10px 14px', marginBottom:20, fontSize:13, color:'#166534', display:'flex', gap:8, alignItems:'flex-start' }}>
                            <span style={{ fontSize:15 }}>ℹ️</span>
                            <span>Each contractor receives a personalized email with their unique invite code and a link to start their contractor onboarding.</span>
                        </div>

                        <div style={{ display:'flex', gap:12, justifyContent:'flex-end' }}>
                            <button style={{ padding:'9px 22px', borderRadius:10, border:'1.5px solid #e5e7eb', background:'#fff', color:'#555', fontSize:14, fontWeight:600, cursor:'pointer' }} onClick={onClose}>Cancel</button>
                            <button
                                style={{ padding:'9px 24px', borderRadius:10, border:'none', background: emailCount && !sending && !(requireLocation && !locationId) ? '#1B75F0' : '#93C5FD', color:'#fff', fontSize:14, fontWeight:700, cursor: emailCount && !sending && !(requireLocation && !locationId) ? 'pointer' : 'not-allowed', display:'flex', alignItems:'center', gap:8 }}
                                onClick={handleSend}
                                disabled={sending || !emailCount || (requireLocation && !locationId)}
                            >
                                {sending
                                    ? <><span style={{ display:'inline-block', width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} /> Sending…</>
                                    : `Send ${emailCount > 0 ? emailCount : ''} Invite${emailCount !== 1 ? 's' : ''} →`
                                }
                            </button>
                        </div>
                    </>
                ) : results.error ? (
                    <div>
                        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', color:'#991b1b', fontSize:14, marginBottom:16 }}>✗ {results.error}</div>
                        <button style={{ padding:'8px 20px', borderRadius:10, border:'1.5px solid #e5e7eb', background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }} onClick={() => setResults(null)}>← Try Again</button>
                    </div>
                ) : (
                    <div>
                        <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'14px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
                            <span style={{ fontSize:22 }}>🎉</span>
                            <div>
                                <div style={{ fontWeight:700, color:'#166534', fontSize:15 }}>{results.sent} invite{results.sent !== 1 ? 's' : ''} sent!</div>
                                <div style={{ fontSize:12, color:'#4ade80', marginTop:2 }}>Contractor invite emails are on their way.</div>
                            </div>
                        </div>
                        <div style={{ maxHeight:200, overflowY:'auto', marginBottom:20, border:'1px solid #f0f0f0', borderRadius:10, overflow:'hidden' }}>
                            {results.results?.map((r, i) => (
                                <div key={i} style={{ fontSize:13, padding:'10px 14px', borderBottom:'1px solid #f5f5f5', display:'flex', justifyContent:'space-between', alignItems:'center', background: i%2===0 ? '#fff' : '#fafafa' }}>
                                    <div>
                                        <div style={{ fontWeight:500 }}>{r.email}</div>
                                        {r.token && <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>Code: <code style={{ background:'#f3f4f6', padding:'1px 5px', borderRadius:4 }}>{r.token}</code></div>}
                                    </div>
                                    <span style={{ color: r.status==='sent'?'#22c55e':'#ef4444', fontWeight:700, fontSize:13 }}>
                                        {r.status==='sent' ? '✓ Sent' : '✗ Failed'}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <button style={{ padding:'9px 24px', borderRadius:10, border:'none', background:'#1B75F0', color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer' }} onClick={onClose}>Done</button>
                    </div>
                )}
            </div>
        </div>
    );
}

const CONTRACTOR_PAY_BADGE = (payType) => {
    const pt = payType || 'Hourly';
    const styles = {
        Hourly:  { bg: '#e3f2fd', color: '#1565c0', icon: '⏱' },
        FlatFee: { bg: '#e8f5e9', color: '#2e7d32', icon: '📋' },
        Weekly:  { bg: '#fce4ec', color: '#880e4f', icon: '📅' },
        Monthly: { bg: '#fff3e0', color: '#e65100', icon: '🗓' },
    };
    const s = styles[pt] || { bg: '#f3f4f6', color: '#374151', icon: '💼' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: s.bg, color: s.color,
            fontWeight: 600, fontSize: 11, padding: '3px 9px',
            borderRadius: 20, whiteSpace: 'nowrap',
        }}>
            {s.icon} {pt}
        </span>
    );
};

const headers = [
    { headerName: "Code", key: "code" },
    { headerName: "Name", key: "name" },
    { headerName: "Email", key: "email" },
    { headerName: "Pay Type", key: "pay_type" },
    { headerName: "Active Status", key: "active_status" },
    { headerName: "Start Date", key: "startDate" },
    { headerName: "", key: "action" },
];

function Contractor() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm]       = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [activeFilter, setActiveFilter]   = useState('active');
    const { data: contractors = [], isLoading } = useQuery({ queryKey: ['contractors'], queryFn: getContractors });

    const companyId = localStorage.getItem('companyId');
    const { data: pendingAccounts = [] } = useQuery({
        queryKey: ['pending-approvals', companyId],
        queryFn: async () => (await axiosInstance.get(`/auth/pending-approvals/${companyId}`)).data ?? [],
        enabled: !!companyId,
    });
    const pendingContractorIds = new Set(pendingAccounts.filter(u => u.role === 'Contractor').map(u => u.contractorId).filter(Boolean));

    const isPendingReview = (c) => pendingContractorIds.has(c.id) || c.onboardStatus === 'pending_review' || c.onboardingStatus === 'pending_review';

    const approvedContractors = contractors.filter(c => !isPendingReview(c));

    const setupNeededCount = approvedContractors.filter(c =>
        !c.payRate || Number(c.payRate) === 0
    ).length;

    const todayStart = new Date(); todayStart.setHours(23, 59, 59, 999);
    const terminatedCount = approvedContractors.filter(c => !!(c.terminationDate && new Date(c.terminationDate) <= todayStart)).length;
    const activeCount     = approvedContractors.filter(c => c.active && !(c.terminationDate && new Date(c.terminationDate) <= todayStart)).length;

    const tableData = useMemo(() => contractors
        .filter(c => !isPendingReview(c))
        .filter(c => {
            const terminated = !!(c.terminationDate && new Date(c.terminationDate) <= todayStart);
            const needsSetup = !c.payRate || Number(c.payRate) === 0;
            if (activeFilter === 'terminated') return terminated;
            if (activeFilter === 'setup') return needsSetup && !terminated;
            if (activeFilter === 'active') return !terminated;
            return true;
        })
        .filter(c => !searchTerm || `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) || (c.email||'').toLowerCase().includes(searchTerm.toLowerCase()) || (c.id||'').toLowerCase().includes(searchTerm.toLowerCase()))
        .map(c => {
            const needsSetup = !c.payRate || Number(c.payRate) === 0;
            const terminated = !!(c.terminationDate && new Date(c.terminationDate) <= todayStart);
            const statusLabel = terminated ? 'Terminated' : c.active ? 'Active' : 'Inactive';
            const statusColor = terminated ? '#6b7280' : c.active ? '#1B75F0' : '#9ca3af';
            return {
                id: c.id,
                code: c.id ? c.id.split('_')[0].toUpperCase() + '-' + c.id.slice(-6).toUpperCase() : '—',
                name: (
                    <span style={{ display:'flex', alignItems:'center', gap:7 }}>
                        {`${c.firstName} ${c.lastName}`}
                        {needsSetup && !terminated && (
                            <span style={{ background:'#1B75F0', color:'#fff', fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20, letterSpacing:'0.03em', whiteSpace:'nowrap' }}>
                                SETUP NEEDED
                            </span>
                        )}
                        {terminated && (
                            <span style={{ background:'#6b7280', color:'#fff', fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20, letterSpacing:'0.03em', whiteSpace:'nowrap' }}>
                                TERMINATED
                            </span>
                        )}
                    </span>
                ),
                email: c.email || '—',
                pay_type: CONTRACTOR_PAY_BADGE(c.payType),
                residence: [c.addressLine1, c.city, c.state].filter(Boolean).join(', ') || '—',
                active_status: (
                    <span style={{ background: terminated ? '#f3f4f6' : c.active ? '#EFF6FF' : '#f3f4f6', color: statusColor, fontWeight:600, fontSize:12, padding:'3px 12px', borderRadius:20, border:`1.5px solid ${statusColor}` }}>
                        {statusLabel}
                    </span>
                ),
                startDate: c.startDate ? new Date(c.startDate).toLocaleDateString() : '—',
            };
        }), [contractors, searchTerm, todayStart, activeFilter]);

    const cardFilters = [
        { title: 'Active Contractor',      count: activeCount,      icon: <PersonIcon />,   filter: 'active' },
        { title: 'Onboarding Blocking',    count: 0,                icon: <PersonsIcon />,  filter: null },
        { title: 'Setup Needed',           count: setupNeededCount, icon: <UserChecked />,  filter: 'setup' },
        { title: 'Terminated Contractors', count: terminatedCount,  icon: <UserDisabled />, filter: 'terminated' },
    ];

    return (
        <div className='content' style={{ padding: "16px 24px 16px 16px" }}>
            {showInviteModal && <ContractorInviteModal onClose={() => setShowInviteModal(false)} />}
            <div className="start-new-payroll-section">
                <div>
                    <div className="row mt-4">
                        {cardFilters.map((item) => (
                            <Card
                                key={item.title}
                                title={item.title}
                                count={item.count}
                                icon={item.icon}
                                isImage={false}
                                isActive={!!item.filter && activeFilter === item.filter}
                                onClick={() => item.filter && setActiveFilter(f => f === item.filter ? 'active' : item.filter)}
                            />
                        ))}
                    </div>
                    {activeFilter === 'terminated' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                            <span style={{ fontSize: 13, color: '#6b7280' }}>Showing terminated contractors</span>
                            <button
                                onClick={() => setActiveFilter('active')}
                                style={{ fontSize: 12, color: '#1B75F0', fontWeight: 600, background: 'none', border: '1px solid #1B75F0', borderRadius: 20, padding: '2px 12px', cursor: 'pointer' }}
                            >
                                ✕ Clear filter
                            </button>
                        </div>
                    )}
                    {activeFilter === 'setup' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                            <span style={{ fontSize: 13, color: '#6b7280' }}>Showing contractors with incomplete setup</span>
                            <button
                                onClick={() => setActiveFilter('active')}
                                style={{ fontSize: 12, color: '#1B75F0', fontWeight: 600, background: 'none', border: '1px solid #1B75F0', borderRadius: 20, padding: '2px 12px', cursor: 'pointer' }}
                            >
                                ✕ Clear filter
                            </button>
                        </div>
                    )}
                </div>
                <div>
                    <div className='d-flex justify-content-between mt-5 mb-3' style={{ flexWrap: 'wrap', gap: 8 }}>
                        <div className='d-flex align-items-center gap-3'>
                            <Button startIcon={<img src={PlusIcon} alt="add" />} onClick={() => navigate(stringMerger(routes.CONTRACTOR.HOME, routes.CONTRACTOR.ADD_EDIT_CONTRACTOR))}>Add Contractor</Button>
                            <Button
                                onClick={() => setShowInviteModal(true)}
                                style={{ background:'#EFF6FF', color:'#1B75F0', border:'1.5px solid #1B75F0', borderRadius:10, fontWeight:600, fontSize:14, padding:'8px 18px', cursor:'pointer', display:'flex', alignItems:'center', gap:7 }}
                            >
                                ✉️ Send Onboarding Invites
                            </Button>
                            <ToggleSwitch />
                        </div>
                        <Input placeholder='Search by Name, Email, or Id' startIcon={search} style={{ width: "100%", maxWidth: "380px" }} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                    {isLoading ? <p className="text-center mt-4">Loading contractors...</p> : <Table headers={headers} data={tableData} handleView={(d) => navigate(`/contractor/view?code=${d.id}`)} handleEdit={(d) => navigate(`/contractor/add-edit-contractor?id=${d.id}`)} />}
                </div>
            </div>
        </div>
    );
}
export default Contractor;


